import React, { useRef, useState } from 'react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { useToast } from '@/components/ui/use-toast'
import { formDataFetcher } from 'api/swr'
import { Check, Copy, ExternalLink } from 'lucide-react'

type UploadResult = {
  fileName: string
  destination: string
  publicUrl: string
  cdnUrl: string
}

type UploadResponse = {
  items: UploadResult[]
}

const BUCKET = 'text-sticker-maker-c4368-br'
const GCS_PATH = 'audio/stories'
const GCS_CONSOLE_URL = `https://console.cloud.google.com/storage/browser/${BUCKET}/${GCS_PATH}`

const UPLOAD_BATCH_SIZE = 50

export default function UploadStoryAudioTab() {
  const { toast } = useToast()
  const inputRef = useRef<HTMLInputElement>(null)
  const [uploading, setUploading] = useState(false)
  const [results, setResults] = useState<UploadResult[]>([])
  const [copiedUrl, setCopiedUrl] = useState<string | null>(null)

  const onSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files
    if (!files || files.length === 0) return

    const fileList = Array.from(files)
    const batches: File[][] = []
    for (let i = 0; i < fileList.length; i += UPLOAD_BATCH_SIZE) {
      batches.push(fileList.slice(i, i + UPLOAD_BATCH_SIZE))
    }

    try {
      setUploading(true)
      const allItems: UploadResult[] = []

      for (const batch of batches) {
        const formData = new FormData()
        batch.forEach((file) => {
          formData.append('files', file)
        })
        const res = (await formDataFetcher('/debug-tools/gcs/upload-story-audio', {
          arg: formData,
        })) as UploadResponse
        allItems.push(...res.items)
      }

      setResults(allItems)
      toast({
        title: '上传成功',
        description: `已上传 ${allItems.length} 个文件到 ${BUCKET}/${GCS_PATH}`,
      })
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : String(err)
      toast({
        variant: 'destructive',
        title: '上传失败',
        description: message,
      })
    } finally {
      setUploading(false)
      if (inputRef.current) inputRef.current.value = ''
    }
  }

  const copyUrl = async (url: string) => {
    try {
      await navigator.clipboard.writeText(url)
      setCopiedUrl(url)
      setTimeout(() => setCopiedUrl(null), 2000)
    } catch {
      toast({
        variant: 'destructive',
        title: '复制失败',
      })
    }
  }

  return (
    <div className="max-w-2xl space-y-6 rounded-lg border p-6">
      <div className="space-y-2 text-sm text-muted-foreground leading-relaxed">
        <p>
          上传故事线 ZIP 资源到 GCS 桶{' '}
          <code className="rounded bg-muted px-1.5 py-0.5 text-xs">{BUCKET}</code>{' '}
          路径{' '}
          <code className="rounded bg-muted px-1.5 py-0.5 text-xs">
            {GCS_PATH}
          </code>
          。上传后 ACL 设为公开读（allUsers = Reader），上传账号为 Owner。
        </p>
        <a
          href={GCS_CONSOLE_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1 text-xs text-primary hover:underline"
        >
          在 GCS 控制台查看
          <ExternalLink className="h-3 w-3" />
        </a>
      </div>

      <div className="space-y-2">
        <Label htmlFor="story-audio-files">ZIP 文件</Label>
        <Input
          ref={inputRef}
          id="story-audio-files"
          type="file"
          multiple
          accept=".zip,application/zip,application/x-zip-compressed"
          disabled={uploading}
          className="hidden"
          onChange={(e) => void onSelect(e)}
        />
        <p className="text-xs text-muted-foreground">
          支持多选 ZIP，单次最多 100 个，单文件最大 100MB。保留原始文件名（特殊字符会替换为下划线）。
        </p>
      </div>

      <Button
        disabled={uploading}
        onClick={() => inputRef.current?.click()}
      >
        {uploading ? '上传中…' : '选择并上传'}
      </Button>

      {results.length > 0 ? (
        <div className="space-y-3">
          <p className="text-sm font-medium">上传结果</p>
          {results.map((item) => (
            <div
              key={item.destination}
              className="rounded-md border bg-muted/40 p-4 text-sm space-y-2"
            >
              <p>
                <span className="font-medium">文件：</span>
                <code className="text-xs">{item.fileName}</code>
              </p>
              <p>
                <span className="font-medium">路径：</span>
                <code className="text-xs">{item.destination}</code>
              </p>
              <div className="flex items-start gap-2">
                <div className="min-w-0 flex-1">
                  <span className="font-medium">公开 URL：</span>
                  <a
                    href={item.publicUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block truncate text-xs text-primary hover:underline"
                  >
                    {item.publicUrl}
                  </a>
                </div>
                <Button
                  variant="outline"
                  size="icon"
                  className="shrink-0"
                  onClick={() => void copyUrl(item.publicUrl)}
                >
                  {copiedUrl === item.publicUrl ? (
                    <Check className="h-4 w-4" />
                  ) : (
                    <Copy className="h-4 w-4" />
                  )}
                </Button>
              </div>
              <p className="text-xs text-muted-foreground">
                CDN：{item.cdnUrl}
              </p>
            </div>
          ))}
        </div>
      ) : null}
    </div>
  )
}
